import { differenceInDays } from 'date-fns';
import { makeAutoObservable } from 'mobx';

import { fetchAvailableDocumentOfTruthTypes } from 'api/billing';
import { countryIds } from 'constants/routeParams';
import { SiteFeaturesIDs } from 'store/SiteFeatures/SiteFeatures';
import { PopupIds } from 'store/UIStore/Popups';
import { etrnBannerClosedTimestampStorage, etrnPopupClosedTimestampStorage } from 'utils/storage/Storage';

import { RootStore } from '../RootStore';

const repeatShowIntervalDays = 7;

const excludedOwnershipIds = [0, 25, 76];

class EtrnPromoStore {
  root;
  bannerClosedTimestamp: number = etrnBannerClosedTimestampStorage.get();
  popupClosedTimestamp: number = etrnPopupClosedTimestampStorage.get();
  hasDataMatchPoint: boolean | null = null;

  constructor(root: RootStore) {
    this.root = root;

    makeAutoObservable(this);
  }

  get isFeatureEnabled() {
    return Boolean(this.root.siteFeatures.isSiteFeatureEnabled[SiteFeaturesIDs.etrnReadyBanner]);
  }

  get isAvailableForUser() {
    const profile = this.root.profile.data?.profile;

    const isRussianAccount = profile?.countryId === Number(countryIds.russia);
    const isCompanyOrSoleProprietor =
      profile?.ownershipId !== undefined && !excludedOwnershipIds.includes(profile.ownershipId);

    return isRussianAccount && isCompanyOrSoleProprietor;
  }

  get canShowPromo() {
    return this.isFeatureEnabled && this.isAvailableForUser && this.hasDataMatchPoint === true;
  }

  get isBannerDismissed() {
    if (!this.bannerClosedTimestamp) return false;

    return differenceInDays(Date.now(), this.bannerClosedTimestamp) < repeatShowIntervalDays;
  }

  get isBannerVisible() {
    return this.canShowPromo && !this.isBannerDismissed;
  }

  closeBanner = () => {
    this.bannerClosedTimestamp = Date.now();
    etrnBannerClosedTimestampStorage.set(this.bannerClosedTimestamp);
  };

  get isPopupDismissed() {
    if (!this.popupClosedTimestamp) return false;

    return differenceInDays(Date.now(), this.popupClosedTimestamp) < repeatShowIntervalDays;
  }

  showPopupAfterCounterOffer = () => {
    if (!this.canShowPromo || this.isPopupDismissed) return;

    this.root.ui.popups.open(PopupIds.etrnReadyPopup);
  };

  closePopup = () => {
    this.popupClosedTimestamp = Date.now();
    etrnPopupClosedTimestampStorage.set(this.popupClosedTimestamp);
    this.root.ui.popups.close(PopupIds.etrnReadyPopup);
  };

  setHasDataMatchPoint = (value: boolean) => {
    this.hasDataMatchPoint = value;
  };

  fetchConfirmationStatus = async () => {
    if (!this.isFeatureEnabled || !this.isAvailableForUser) return;

    try {
      const { hasDataMatchPoint } = await fetchAvailableDocumentOfTruthTypes();

      this.setHasDataMatchPoint(hasDataMatchPoint);
    } catch (error) {
      console.error(error);
    }
  };
}

export { EtrnPromoStore };
